
<script>
import { defineComponent, computed, inject, ref, nextTick } from 'vue'
import { useStore } from 'vuex'
import { inBrowser } from '@kit/utils/EnvironmentHelper'
import { contactUsFormEndpoint, apiSetup } from '@kit/utils/APIV1'
import Column from '@kit/components/Column.vue'
import ParallaxHeader from '@kit/components/ParallaxHeader.vue'
import { pathname } from '@kit/utils/Formats'
import Reveal from '@kit/components/Reveal.vue'
import Shine from '@kit/components/Shine.vue'
import { modal, errorModal, greenButton } from '@project/themes'
import Modal from '@kit/components/Modal.vue'
import IconButton from '@kit/components/IconButton.vue'
import "@kit/assets/css/superbox-core-x.css"
import '@css/index.css'
import "primevue/resources/themes/saga-blue/theme.css"
import "primevue/resources/primevue.min.css"
import "primeicons/primeicons.css"

import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'
import Dropdown from 'primevue/dropdown'

export default defineComponent({
  name: "Contact",
  setup() {
    const hydrate = inject("hydrate")
    const themer = inject("themer")
    const store = useStore()
    const inMobile = computed(() => store.state.inMobile)
    const smoothScroll = inject('smoothScroll')
    const showForm = ref(false)
    const header = ref(null)

    const modal_ = themer(modal)
    const errorModal_ = themer(errorModal)
    const greenButton_ = themer(greenButton)
    const formDataPacket = ref(null)
    const formServerErrorMessage = ref(null)

    apiSetup()

    const scrollToTop = () => {
      if(inBrowser) {
       const errorHeader = document.getElementById('contact_error-header')
       smoothScroll({ scrollTo:errorHeader })
      }
    }

    const { prefetch } = hydrate({
      prefetch:["contactUs"],
      mounted() {
        if(inBrowser) {
          showForm.value = true
        }
      },
      metadata(meta, { contactUs }) {
        const seo = contactUs.seo_set[0]
        meta.addTitle(seo.page_title)
        meta.addCanonical()
        meta.addMetaItem([
          { name:'description', content: seo.page_description },
          { name:'og:title', content: seo.open_graph_title },
          { name:'og:description', content: seo.open_graph_description },
          { name:'og:image', content: pathname(seo.open_graph_image.guid) },
          { name:'og:image:alt', content: seo.open_graph_image_alt } 
        ])
      },
      preload: ({contactUs}) => [pathname(contactUs.header_image.guid)],
      watch({ Contact }, watchReveal) {
        watchReveal(header,
          () => (Contact.preload == 1 && Contact.prefetch == 1)
        )
      }
    })

    const contactUs = computed(() => {
      return prefetch.value ? prefetch.value.contactUs : null
    })

    const dialogWidth = computed(() => {
      return inMobile.value ? '80vw' : '50vw'
    })

    const name = ref({ model:"", error:""})
    const email = ref({ model:"", error:""})
    const message = ref({model:"", error:""})
    const phoneNumber = ref({model:"", error:""})
    const showSuccessModal = ref(false)
    const showFailureModal = ref(false)
    const dialogHeader = ref('')
    const dialogMessage = ref('')
    const hasErrors = ref(false)
    const loading = ref(false)

    const submitForm = async() => {
      const nameVal = name.value.model
      const phoneNumberVal = phoneNumber.value.model
      const emailVal = email.value.model
      const messageVal = message.value.model

      name.value.error = ""
      phoneNumber.value.error = "" 
      email.value.error = ""
      message.value.error = ""
    
      hasErrors.value = false

      let errors = false;
      if(!nameVal) {
        errors = true;
        name.value.error = 'Name is required'
      }
      if(!emailVal) {
        errors = true;
        email.value.error = 'Email is required'
      }
      if(!messageVal) {
        errors = true 
        message.value.error = 'Message is required'
      }
      if(!phoneNumberVal) {
        errors = true 
        phoneNumber.value.error = 'Phone number is required'
      }


      if(!errors) {

        const data = { 
          contact_name: nameVal, 
          email: emailVal, 
          message: messageVal, 
          phone_number: phoneNumberVal,
          profile:"contactUs",
        }

        formDataPacket.value = data

        loading.value = true
        const response = await contactUsFormEndpoint(data)
        loading.value = false

        if(!response.success) {
          formServerErrorMessage.value = response.message
          showFailureModal.value = true
        } else {
          showSuccessModal.value = true
        }

        name.value.model = ""
        phoneNumber.value.model = "" 
        email.value.model = ""
        message.value.model = ""

      } else {
        hasErrors.value = true
        await nextTick()
        scrollToTop()
      }

    }


    const errorClass = (what) => {
      return what.error ? ["p-invalid"] : [];
    }

    return { 
      pathname, inBrowser, showForm, 
      hasErrors, loading, showSuccessModal, showFailureModal,
      dialogWidth, dialogHeader, dialogMessage, 
      contactUs, submitForm, errorClass, 
      message, name, email, phoneNumber,
      modal_, greenButton_, errorModal_,
      header, formServerErrorMessage, formDataPacket
    }

  },
  components: {
    Dropdown,
    InputText,
    Textarea,
    IconButton,
    Button,
    Modal,
    ProgressBar,
    Column,
    ParallaxHeader,
    Reveal,
    Shine,
    IconButton
  }
});
</script>

<style>
.p-component {
  color:#444444;
  font-family: 'Source Serif Pro', serif !important;
}

.p-inputwrapper {
  background: #EEEEEE !important;
  border-radius: 3px !important;
  padding:10px;
}
.p-inputwrapper-focus {
 border: none !important;
 background: #d8f0c0 !important;
}
.p-dropdown {
  border: none;
  box-shadow: none !important;
} 
.p-inputtext,
.p-textarea,
.p-button {
  border: none;
  box-shadow: none !important;
  background:#EEEEEE;
  padding:15px;
  border-radius:3px !important
}

.p-inputtext::placeholder, .p-textarea::placeholder, .p-component > .p-dropdown-label.p-placeholder {
  color: #444444;
  opacity: 1;
}

.p-button {
  color: #ffffff;
  background: #5f9c43;
}

.p-inputtext:enabled:focus, .p-textarea:enabled:focus {
  background: #d8f0c0 !important;
}
.p-inputtext.p-invalid:enabled:focus, .p-textarea:enabled:focus {
  background: #ffffff !important;
}
.p-button:enabled:hover {
    background: #85c269;
    color: #ffffff;
}

.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    background: #d8f0c0;
}

.p-progressbar .p-progressbar-value {
    background: #85c269;
}

</style>

<style scoped>


  .headline {
    text-transform: uppercase;
  }
  .main-text {
    font-size: 1.2rem;
    line-height: 1.5;
  }
  .header-image-wrapper {
    position:absolute; 
    height:600px; 
    min-height:100%; 
    top:0px; 
    left:0px; 
    right:0px;  
    z-index:1;
  }

  .error { 
    /* background: rgba(255, 255, 255, 0.6); */
    background: rgb(205,22,22);
    background: linear-gradient(90deg, rgba(205,22,22,1) 0%, rgba(242,47,108,1) 100%);
    padding:5px;
    padding-top:8px;
    margin-top:0px !important;
    border-radius: 0px 0px 5px 5px
  } 
  .error::before {
    content:"";
    position:absolute;
    top:0px;
    height:3px;
    background: linear-gradient(90deg, rgb(238, 46, 46) 0%, rgba(242,47,108,1) 100%);
    left:0px;
    right:0px;
  }
  .error > .sb {
    font-size:0.8rem;
    font-weight: bold;
    /* color: #f44336; */
    color:#FFFFFF;
  } 

  h2.error-header {
    font-size:1.2rem;
    /* background:white; */
    background: rgb(205,22,22);
    background: linear-gradient(90deg, rgba(205,22,22,1) 0%, rgba(242,47,108,1) 100%);
    color:white;
    text-align:center;
    padding:15px;
    padding-bottom:25px;
  }
  h2.error-header::after {
    content:"";
    position: absolute;
    bottom:0px;
    left:0px;
    right:0px;
    height:10px;
    /* background:#f44336; */
    background: linear-gradient(90deg, rgb(238, 46, 46) 0%, rgba(242,47,108,1) 100%);
  }

  .green {
    background:#609642;
    color: white;
  }

</style>

<style>

  .contact__alpha-mask {
    mask-type: alpha;
    mask-image: linear-gradient(black, transparent);
    -webkit-mask-image: linear-gradient(black, transparent);
  }

  .contact__white-gradient {
    background:rgba(100, 0, 38, 0.8);
  }


 
</style>


<template>
  <main class="sb sb-v">
    <Reveal ref="header" class="sb sb-v" :delayLoader="500">
      <template v-slot:reveal>
        <Column tag="article" gtB="800" ltD="60" ltT="95" class="sb-v sb-g20" style="padding-top:150px; padding-bottom:50px; z-index:2;">
          <h1 class="sb sb-text headline page-header-plain" :style="`color:${contactUs.header_text_color};`">{{ contactUs.display_title }}</h1>
          <p class="sb sb-text main-text" :style="`color:${contactUs.header_text_color};`" v-html="contactUs.main_text"></p>
        </Column>
        <ParallaxHeader
          class="header-image-wrapper contact__alpha-mask"
          :parallaxTheme="{ parallax: { background:false, type:'basic' }}"
          :image="contactUs.header_image.guid"
          :ariaImgLabel="contactUs.aria_header_image_label"
          cover2Classes="contact__white-gradient"
        />
      </template>
      <template v-slot:loader>
        <Shine style="margin-top:70px;" min="300px" h="100px" shine="dark" />
      </template>
    </Reveal>
    
    <Column tag="form" ltD="70" gtB="1000" ltT="90"  class="sb-v sb-g20" style="padding-bottom:40px;" aria-label="Contact Form">

      <h2 v-if="hasErrors" id="contact_error-header" class="sb sb-v error-header">
        The form has errors. Please fix and resubmit.
      </h2>

      <div class="sb sb-v">
        <InputText placeholder="Name" 
          id="contactus-name" 
          type="text" 
          aria-label="First name text input"
          v-model="name.model" 
          aria-errormessage="contactus-name-err" :aria-invalid="name.error"
          :class="errorClass(name)"
        />
        <div class="sb sb-h error" v-show="name.error" :aria-hidden="!name.error">
          <small 
            class="sb"
            id="contactus-name-err"
          >{{ name.error }}</small>
        </div>
      </div>

      <div class="sb sb-v">
        <InputText placeholder="Email" 
          id="contactus-email" 
          type="email" 
          aria-label="Email text input"
          v-model="email.model" 
          aria-errormessage="contactus-email-err" :aria-invalid="name.error"
          :class="errorClass(email)"
        />
        <div class="sb sb-h error" v-show="email.error" :aria-hidden="!email.error">
          <small 
            class="sb"
            id="contactus-email-err"
          >{{ email.error }}</small>
        </div>
      </div>

      <div class="sb sb-v">
        <InputText placeholder="Phone Number" 
          id="contactus-phone-number" 
          type="text" 
          aria-label="Phone number text input"
          v-model="phoneNumber.model" 
          aria-describedby="contactus-phone-number-err" 
          :class="errorClass(phoneNumber)"
        />
        <div class="sb sb-h error" v-show="phoneNumber.error" :aria-hidden="!phoneNumber.error">
          <small 
            class="sb"
            id="contactus-phone-number-err"
          >{{ phoneNumber.error }}</small>
        </div>
      </div>

      <div class="sb sb-v">
        <Textarea placeholder="Your Message" 
          id="contactus-message" 
          type="text" 
          aria-label="Message text area"
          v-model="message.model" 
          aria-describedby="contactus-message-err" 
          :autoResize="true"
          :rows="5"
          :class="errorClass(message)"
        />
        <div class="sb sb-h error" v-show="message.error" :aria-hidden="!message.error">
          <small 
            class="sb"
            id="contactus-message-err"
          >{{ message.error }}</small>
        </div>
      </div>

      <div class="sb sb-v">
        <!-- <Button label="Submit" :disabled="loading" @click="submitForm" icon="pi pi-check" /> -->
        <IconButton 
          :theme="greenButton_" 
          text="Submit" 
          :loading="loading" 
          @buttonClick="submitForm" 
          :isFrugal="false"
          iconDY="15px"
          icon="rounded-check_30"/>
        <ProgressBar mode="indeterminate" v-if="loading" style="height:0.5em; margin-top:5px;" />
      </div>

    </Column>

    <Modal :theme="modal_" :vw="50" :vh="50" :mvw="85" :mvh="60" v-model="showSuccessModal">
      <template v-slot:headline>
        <h4 class="sb sb-text">Thank you for reaching out!</h4>
      </template>
      <template v-slot:body>

        <div v-if="formDataPacket" class="sb sb-v sb-greedy sb-align-c sb-alt2 sb-g20">
          <div class="sb sb-v">
            <h3 class="sb sb-text error-screen-headline">Your message is on its way!</h3>
            <h4 class="sb sb-text">Name: {{ formDataPacket.contact_name }}</h4>
            <h4 class="sb sb-text">Phone: {{ formDataPacket.phone_number }}</h4>
            <h4 class="sb sb-text">Email: {{ formDataPacket.email }}</h4>
          </div>  
        </div>

      </template>
    </Modal>  


    <Modal :theme="errorModal_" :vw="50" :vh="50" :mvw="85" :mvh="60" v-model="showFailureModal">
      <template v-slot:headline>
        <h4 class="sb sb-text">There was a problem</h4>
      </template>
      <template v-slot:body>

        <div v-if="formServerErrorMessage" class="sb sb-v sb-greedy sb-align-c sb-alt2 sb-g20">
          <div class="sb sb-v">
            <h3 class="sb sb-text error-screen-headline">We encountered a problem:</h3>
            <h4 class="sb sb-text" v-html="formServerErrorMessage"></h4>
            <h4 class="sb sb-text">Please reload the page and try again</h4>
          </div>  
        </div>

      </template>
    </Modal> 



  </main>

</template>